import { useApi } from "@/hooks/useApi";
import Image from "next/image";
import { useEffect, useState } from "react";
import { IoCloseSharp } from "react-icons/io5";

interface ScoreModalProps {
	contentId: string;
	isEpisode: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

export default function RemoveScoreModal({
	contentId,
	isEpisode,
	onClose,
	onSuccess,
}: ScoreModalProps) {
	const [score, setScore] = useState<number | null>(null);
	const [loading, setLoading] = useState(true);
	const [deleting, setDeleting] = useState(false);
	const { call } = useApi();
	const endpoint = isEpisode ? "/api/total-score/episode" : "/api/total-score";

	useEffect(() => {
		const fetchScore = async () => {
			try {
				const data = await call(
					`${endpoint}?contentId=${encodeURIComponent(contentId)}`,
					{
						method: "GET",
						headers: { "Content-Type": "application/json" },
					},
					false,
				);
				setScore(data.score ?? null);
			} catch (error) {
				console.error("Failed to fetch score:", error);
				setScore(null);
			} finally {
				setLoading(false);
			}
		};

		fetchScore();
	}, [contentId, endpoint]);

	// Handle delete
	const handleDelete = async () => {
		setDeleting(true);
		try {
			const response = await fetch(endpoint, {
				method: "DELETE",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ contentId }),
			});

			if (response.ok) {
				setScore(null);
			}
			onSuccess();
		} catch (error) {
			console.error("Failed to delete score:", error);
		} finally {
			setDeleting(false);
		}
	};

	return (
		<div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col justify-center items-center z-50">
			<button
				type="button"
				className="absolute top-5 right-5 text-white text-3xl hover:text-gray-300"
				onClick={onClose}
			>
				<IoCloseSharp className="w-6 h-6" />
			</button>

			<div className="flex flex-col items-center">
				<div className="w-full mb-6">
					<Image
						className="m-auto"
						src="/cutin.svg"
						alt="Cutin"
						width={160}
						height={28}
					/>
				</div>

				<hr className="my-[18px] mx-auto w-[360px]" />

				<div className="bg-gray-900 rounded-lg p-8 w-[360px] text-center">
					{loading ? (
						<p className="text-white text-lg">در حال بارگذاری...</p>
					) : score !== null ? (
						<>
							<p className="text-white text-2xl font-bold mb-4">
								مقدار امتیازی که کسر می‌گردد:
								<span className="text-pink-500 mr-2">{score}</span>
							</p>
							<p className="text-gray-300 mb-8">
								آیا از حذف این امتیاز اطمینان دارید؟
							</p>

							<div className="flex justify-center gap-4">
								<button
									disabled={deleting}
									onClick={handleDelete}
									className="px-8 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50"
								>
									{deleting ? "در حال حذف..." : "حذف امتیاز"}
								</button>
								<button
									onClick={onClose}
									disabled={deleting}
									className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50"
								>
									لغو
								</button>
							</div>
						</>
					) : (
						<p className="text-white text-xl">
							شما هنوز امتیازی برای این محتوا ثبت نکرده‌اید.
						</p>
					)}
				</div>
			</div>
		</div>
	);
}
